import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import SportLabel from '../../cards/CardComponents/SportLabel';
import { sportTypeIds } from '../../../utils/eventConstants';
import breadCrumbData from '../../../pages/SeoLocationLandingPage/breadCrumb.json';

const SeoURL = ({ profileData }) => {
    const mensTennisRankingURL = '/mens-tennis-rankings';
    const womensTennisRankingURL = '/womens-tennis-rankings';
    const boysTennisRankingURL = '/boys-tennis-rankings';
    const girlsTennisRankingURL = '/girls-tennis-rankings';
    const mensPickleballRankingURL = '/mens-pickleball-rankings';
    const womensPickleballRankingURL = '/womens-pickleball-rankings';
    const boysPickleballRankingURL = '/boys-pickleball-rankings';
    const girlsPickleballRankingURL = '/girls-pickleball-rankings';
    const genderType = {
        MALE: 'Male',
        FEMALE: 'Female',
    };

    const formatString = (str) => str.toLowerCase().replace(/\s+/g, '-');

    const getCountryURL = (countryName) => {
        if (countryName) {
        const formattedCountryName = formatString(countryName);
        if (breadCrumbData[formattedCountryName]) {
            return `/${formattedCountryName}`;
            }
        }
        return '';
    };

    const getStateURL = (countryURL, stateName, formattedCountryName) => {
        if (!countryURL || !stateName) return '';
        const formattedStateName = formatString(stateName);
        if (breadCrumbData[formattedCountryName][formattedStateName]) {
            return `${countryURL}/${formattedStateName}`;
        }
        return '';
    };

    const getCityURL = (stateURL, cityName, formattedCountryName, formattedStateName) => {
        if (!stateURL || !cityName) return '';
        const formattedCityName = formatString(cityName);
        if (breadCrumbData[formattedCountryName][formattedStateName][formattedCityName]) {
            return `${stateURL}/${formattedCityName}`;
        }
        return '';
    };

    const locationHelper = () => {
        const { countryName, stateName, cityName } = profileData?.location || {};

        const countryURL = getCountryURL(countryName);
        const formattedCountryName = countryName ? formatString(countryName) : '';
        const stateURL = getStateURL(countryURL, stateName, formattedCountryName);
        const formattedStateName = stateName ? formatString(stateName) : '';
        const cityURL = getCityURL(stateURL, cityName, formattedCountryName, formattedStateName);

        return {
            countryURL,
            stateURL,
            cityURL,
        };
    };

    const isEighteenOrYounger = (birthDate) => {
        if (!birthDate) {
            return false;
        }
        const birth = new Date(birthDate);
        const today = new Date();
        const age = today.getFullYear() - birth.getFullYear();
        return age <= 18;
    };

    const getGender = (gender) => {
        if (!gender) {
            return 'Unknown';
        }
        return gender.toLowerCase() === genderType.MALE.toLowerCase() ? genderType.MALE : genderType.FEMALE;
    };

    const locationHelperData = locationHelper();
    const isJunior = isEighteenOrYounger(profileData?.birthDate);
    const gender = getGender(profileData?.gender);

    const getRankingURLForGenderAndAge = (gender, isJunior, maleJuniorURL, maleAdultURL, femaleJuniorURL, femaleAdultURL) => {
        if (gender === genderType.MALE) {
            return isJunior ? maleJuniorURL : maleAdultURL;
        } if (gender === genderType.FEMALE) {
            return isJunior ? femaleJuniorURL : femaleAdultURL;
        }
        return '';
    };

    const getRankingURL = (sportType) => {
        if (sportType === sportTypeIds.TENNIS) {
            return getRankingURLForGenderAndAge(gender, isJunior, boysTennisRankingURL, mensTennisRankingURL, girlsTennisRankingURL, womensTennisRankingURL);
        } if (sportType === sportTypeIds.PICKLEBALL) {
            return getRankingURLForGenderAndAge(gender, isJunior, boysPickleballRankingURL, mensPickleballRankingURL, girlsPickleballRankingURL, womensPickleballRankingURL);
        }
        return '';
    };

    const getRankingTitle = (sportName) => {
        let title = '';
        if (gender === genderType.MALE) {
            title = isJunior ? `Junior Boy's ${sportName} Rankings` : `Adult Men's ${sportName} Rankings`;
        } else if (gender === genderType.FEMALE) {
            title = isJunior ? `Junior Girl's ${sportName} Rankings` : `Adult Women's ${sportName} Rankings`;
        } else {
            return '';
        }
        return title;
    };

    const createCompleteURLs = (sportType) => {
        const rankingURL = getRankingURL(sportType);
        const sportName = sportType === sportTypeIds.TENNIS ? 'Tennis' : 'Pickleball';
        const rankingTitle = getRankingTitle(sportName);
        const completeURLs = [];

        if (locationHelperData.countryURL) {
            completeURLs.push({
                url: locationHelperData.countryURL + rankingURL,
                title: `${rankingTitle} ${profileData.location.countryName}`,
            });
        }
        if (locationHelperData.stateURL) {
            completeURLs.push({
                url: locationHelperData.stateURL + rankingURL,
                title: `${rankingTitle} ${profileData.location.stateName}`,
            });
        }
        if (locationHelperData.cityURL) {
            completeURLs.push({
                url: locationHelperData.cityURL + rankingURL,
                title: `${rankingTitle} ${profileData.location.cityName}`,
            });
        }

        return completeURLs;
    };

    const tennisURLs = createCompleteURLs(sportTypeIds.TENNIS);
    // enable once we want to proceed with pickleball ranking urls
    // const pickleballURLs = createCompleteURLs(sportTypeIds.PICKLEBALL);

    if (tennisURLs.length === 0 || !profileData?.gender) {
        return null;
    }

    return (
        <div>
            <div className="utr-card event-card">
                <SportLabel sportTypeId={sportTypeIds.TENNIS} style={{ position: 'relative' }} />
                <div className="mt14 p8">
                    <ul>
                        {tennisURLs.map(urlObj => (
                            <li key={urlObj.title}>
                                <Link to={urlObj.url}>{urlObj.title}</Link>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
            {/* <div className="utr-card event-card">
                <SportLabel sportTypeId={sportTypeIds.PICKLEBALL} style={{ position: 'relative' }} />
                <div className="mt14 p8">
                    <ul>
                        {pickleballURLs.map(urlObj => (
                            <li key={urlObj.title}>
                                <Link to={urlObj.url}>{urlObj.title}</Link>
                            </li>
                        ))}
                    </ul>
                </div>
            </div> */}
        </div>
    );
};

SeoURL.propTypes = {
    profileData: PropTypes.shape({
        birthDate: PropTypes.string,
        gender: PropTypes.string.isRequired,
        location: PropTypes.shape({
            countryName: PropTypes.string.isRequired,
            stateName: PropTypes.string.isRequired,
            cityName: PropTypes.string.isRequired,
        }).isRequired,
    }).isRequired,
};

export default SeoURL;
